import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const LENGTH = 6;
const SHAKE_MS = 300;

interface CodeInputProps {
  onChange: (code: string) => void;
  onComplete: (code: string) => void;
  shake: boolean;
  onShakeEnd: () => void;
  disabled?: boolean;
}

export const CodeInput: React.FC<CodeInputProps> = ({
  onChange,
  onComplete,
  shake,
  onShakeEnd,
  disabled = false,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(LENGTH).fill(""));
  const boxes = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    boxes.current[0]?.focus();
  }, []);

  useEffect(() => {
    onChange(digits.join(""));
  }, [digits, onChange]);

  useEffect(() => {
    if (!shake) return;
    const id = window.setTimeout(() => {
      setDigits(Array(LENGTH).fill(""));
      boxes.current[0]?.focus();
      onShakeEnd();
    }, SHAKE_MS);
    return () => window.clearTimeout(id);
  }, [shake, onShakeEnd]);

  const fill = (start: number, text: string) => {
    const typed = text.replace(/\D/g, "").slice(0, LENGTH - start);
    if (!typed) return;
    const next = [...digits];
    typed.split("").forEach((d, i) => {
      next[start + i] = d;
    });
    setDigits(next);
    const last = start + typed.length;
    if (next.every(Boolean)) onComplete(next.join(""));
    else boxes.current[Math.min(last, LENGTH - 1)]?.focus();
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Backspace") return;
    e.preventDefault();
    const from = digits[i] ? i : i - 1;
    if (from < 0) return;
    setDigits(digits.map((d, idx) => (idx === from ? "" : d)));
    boxes.current[from]?.focus();
  };

  return (
    <div
      data-testid="code-input"
      data-shake={shake}
      className="code-input flex justify-between gap-2"
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            boxes.current[i] = el;
          }}
          data-testid={`code-input-${i}`}
          aria-label={`Digit ${i + 1}`}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          value={d}
          disabled={disabled}
          readOnly={shake}
          onChange={(e) =>
            fill(i, (e.nativeEvent as InputEvent).data ?? e.target.value)
          }
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text").replace(/\D/g, "");
            fill(text.length >= LENGTH ? 0 : i, text);
          }}
          onKeyDown={(e) => onKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className={cn(
            "w-12 h-14 text-center text-xl font-semibold rounded-lg border",
            "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100",
            "border-slate-200 dark:border-slate-700",
            "focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500",
          )}
        />
      ))}
    </div>
  );
};
